// src/components/planner/modals/DayEventsModal.tsx

import React, { useState } from 'react';
import { X, Pencil, Trash2, Plus, Clock } from 'lucide-react';
import MonthEventModal from './MonthEventModal';
import EditMonthEventModal from './EditMonthEventModal';

const API_BASE_URL = '/api';

interface DayEventsModalProps {
    date: Date;
    events: any[];
    onClose: () => void;
    onReload: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const DayEventsModal: React.FC<DayEventsModalProps> = ({
    date,
    events,
    onClose,
    onReload,
    onShowToast
}) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState<any | null>(null);
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

    const handleDelete = async (eventId: string) => {
        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch(`${API_BASE_URL}/planner/month-events/${eventId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.ok) {
                onShowToast('Evento removido', 'info');
                setConfirmDeleteId(null);
                onReload();
            } else {
                onShowToast('Erro ao remover evento', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao remover evento', 'error');
        }
    };

    // Se abriu modal de adicionar ou editar, mostra ele por cima
    if (showAddModal) {
        return (
            <MonthEventModal
                date={date}
                onClose={() => setShowAddModal(false)}
                onSave={() => {
                    setShowAddModal(false);
                    onReload();
                }}
                onShowToast={onShowToast}
            />
        );
    }

    if (editingEvent) {
        return (
            <EditMonthEventModal
                event={editingEvent}
                onClose={() => setEditingEvent(null)}
                onSave={() => {
                    setEditingEvent(null);
                    onReload();
                }}
                onShowToast={onShowToast}
            />
        );
    }

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>
                        {date.toLocaleDateString('pt-BR', {
                            weekday: 'long',
                            day: '2-digit',
                            month: 'long'
                        })}
                    </h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="plnr-modal-body">
                    {events.length === 0 ? (
                        <p style={{
                            textAlign: 'center',
                            color: 'var(--plnr-text-secondary)',
                            fontSize: '13px',
                            padding: '24px 0'
                        }}>
                            Nenhum evento neste dia
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                            {events.map((event) => (
                                <div key={event._id}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        padding: '10px 12px',
                                        background: 'var(--plnr-bg-hover)',
                                        border: '1px solid var(--plnr-border)',
                                        borderLeft: `4px solid ${event.color || 'var(--platform-accent-light)'}`,
                                        borderRadius: '8px',
                                    }}>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{
                                                margin: 0,
                                                fontWeight: 600,
                                                fontSize: '13px',
                                                color: 'var(--plnr-text-primary)',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {event.title}
                                            </p>
                                            {event.time && (
                                                <span style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px',
                                                    fontSize: '11px',
                                                    color: 'var(--plnr-text-secondary)',
                                                    marginTop: '2px'
                                                }}>
                                                    <Clock size={10} />
                                                    {event.time}
                                                </span>
                                            )}
                                        </div>

                                        <button
                                            className="plnr-rv-btn-icon"
                                            title="Editar evento"
                                            onClick={() => setEditingEvent(event)}
                                        >
                                            <Pencil size={13} />
                                        </button>

                                        <button
                                            className="plnr-rv-btn-icon"
                                            title="Excluir evento"
                                            onClick={() => setConfirmDeleteId(
                                                confirmDeleteId === event._id ? null : event._id
                                            )}
                                            style={confirmDeleteId === event._id
                                                ? { borderColor: '#dc2626', color: '#dc2626' }
                                                : {}}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>

                                    {/* Confirmação de exclusão inline */}
                                    {confirmDeleteId === event._id && (
                                        <div style={{
                                            padding: '10px 12px',
                                            background: 'rgba(220, 38, 38, 0.06)',
                                            border: '1px solid rgba(220, 38, 38, 0.25)',
                                            borderTop: 'none',
                                            borderRadius: '0 0 8px 8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px'
                                        }}>
                                            <span style={{ fontSize: '12px', color: '#dc2626', fontWeight: 600 }}>
                                                Confirmar exclusão?
                                            </span>
                                            <div style={{ display: 'flex', gap: '6px' }}>
                                                <button
                                                    className="plnr-btn danger"
                                                    style={{ padding: '4px 10px', fontSize: '11px' }}
                                                    onClick={() => handleDelete(event._id)}
                                                >
                                                    Excluir
                                                </button>
                                                <button
                                                    className="plnr-btn secondary"
                                                    style={{ padding: '4px 10px', fontSize: '11px' }}
                                                    onClick={() => setConfirmDeleteId(null)}
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <button
                        className="plnr-btn primary"
                        style={{ width: '100%', justifyContent: 'center' }}
                        onClick={() => setShowAddModal(true)}
                    >
                        <Plus size={16} />
                        Adicionar evento neste dia
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DayEventsModal;